// Pure URL-search-params merger used by table widgets (filter UI controls)
// and agent-panel adapters (LLM-driven `apply_filters` results). Empty
// arrays, empty strings, null, and undefined values are pruned so the URL
// stays clean — `?contentCategory=` shouldn't appear after the user clears a
// filter. (This is URL hygiene, distinct from the tRPC transformer which
// preserves `null` on the wire.)
//
// `prev` and `updates` are opaque-shape `Record<string, unknown>` because
// TanStack Router's `validateSearch` callback signature passes the previous
// search untyped. The validator re-coerces fields when the URL is read back;
// the merger only has to produce a "clean" object to write.

export type SearchUpdates = Record<string, unknown>;

const isEmptyValue = (v: unknown): boolean =>
  v === undefined ||
  v === null ||
  v === '' ||
  (Array.isArray(v) && (v as unknown[]).length === 0);

export const mergeSearch = (
  prev: SearchUpdates,
  updates: SearchUpdates
): SearchUpdates => {
  const next: SearchUpdates = { ...prev, ...updates };
  for (const k of Object.keys(next)) {
    if (isEmptyValue(next[k])) delete next[k];
  }
  return next;
};

// Union-merge for the LLM "add to existing" path. Array fields in
// `updates` are appended to the same key in `prev` (de-duplicated,
// order-preserving); scalar fields follow replace semantics.
export const unionSearch = (
  prev: SearchUpdates,
  updates: SearchUpdates
): SearchUpdates => {
  const next: SearchUpdates = { ...prev };
  for (const [k, v] of Object.entries(updates)) {
    const prior = prev[k];
    if (Array.isArray(v) && Array.isArray(prior)) {
      const seen = new Set<unknown>();
      const merged: unknown[] = [];
      for (const x of [...prior, ...v]) {
        if (!seen.has(x)) {
          seen.add(x);
          merged.push(x);
        }
      }
      next[k] = merged;
      continue;
    }
    next[k] = v;
  }
  for (const k of Object.keys(next)) {
    if (isEmptyValue(next[k])) delete next[k];
  }
  return next;
};
